/**
 * @file Access checking methods for the PRO admin (product).
 */

import {
  ALBUM_PRICE_MODE,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../constants/albums';
import { createAlbumRequiresPurchase, isAlbum, isWaldofied } from './album';
import { BASENAME } from '../constants/basenames';
import {
  ALBUM_FOLDER_JOIN_TYPE,
  ALL_PHOTOS_MODE,
  PHOTO_SHARING_MODE,
} from '../constants/album-folder';
import { EVENT_ADDON_ITEM_TYPE } from '../constants/event-items';
import { PRO_REGISTRATION_PERSONA_VALUES } from '../views/auth/RegisterView/helpers';
import { ACCOUNT_PRO_ROLES } from '../constants/account-roles';
import { CURRENCY as CURRENCY_CODE } from '../constants/currency';

/**
 * Determines if the user should be able to access pricing on
 * an album or album folder.
 *
 * @param {Object} entity - The album or album folder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The album or album folder subtype.
 * @returns {boolean} - Whether the user can access the album's or album folder's pricing.
 */
export const canAccessPricing = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const rejectedAlbumSubtypes = [
    ALBUM_SUBTYPE.CAMPS,
    ALBUM_SUBTYPE.SCHOOLS,
    ALBUM_SUBTYPE.GREEK,
    ALBUM_SUBTYPE.EVENTS,
    ALBUM_SUBTYPE.WEDDINGS,
  ];

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return (
    !rejectedAlbumTypes.includes(entity.album_type) &&
    !rejectedAlbumSubtypes.includes(entity.album_subtype) &&
    !createAlbumRequiresPurchase(entity.album_subtype)
  );
};

/**
 * Whether the user can access account pricing based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountPricingOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.PRO];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access account PhotoSearch subscriptions based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessPhotoSearchSubscriptionsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access PhotoManager subscriptions based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessPhotoManagerSubscriptionsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can access account referrals  based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountReferralsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access account creations  based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountCreationsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access account branding based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountBrandingOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can access account branding based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountCopyrightOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [
    BASENAME.SHUTTERBUG,
    BASENAME.CAMPS,
    BASENAME.SCHOOLS,
    BASENAME.SPORTS,
  ];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can access account album pricing (Print Packages
 * and Individual Photo Pricing templates) on their account settings based
 * on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountAlbumPricingOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG, BASENAME.CAMPS];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can create account album pricing items (Print Packages
 * individual Photo Pricing templates, Custom Prints, etc), based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canCreateAccountAlbumPricingsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG, BASENAME.CAMPS];

  return !rejectedBasenames.includes(basename);
};

/**
 * Determines if the user should be able to add/edit Uniform Tags
 * on the album or album folder.
 *
 * @param {Object} entity - The album or album folder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The album's or album folder's subtype.
 * @returns {boolean} - Whether the user can add/edit Uniform Tags on the album or album folder.
 */
export const canAddUniformTags = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const allowedAlbumTypes = [ALBUM_TYPE.PRO];

  const allowedAlbumSubtypes = [
    ALBUM_SUBTYPE.SCHOOLS,
    ALBUM_SUBTYPE.YOUTH_SPORTS,
    ALBUM_SUBTYPE.CAMPS,
    ALBUM_SUBTYPE.SPORTS,
    ALBUM_SUBTYPE.CHURCH,
    null, // Generic PRO
  ];

  return (
    allowedAlbumTypes.includes(entity.album_type) &&
    allowedAlbumSubtypes.includes(entity.album_subtype)
  );
};

/**
 * Determines if the user should be able to add/edit Grade Tags
 * on the album or album folder.
 *
 * @param {Object} entity - The album or album folder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The album's or album folder's subtype.
 * @returns {boolean} - Whether the user can add/edit Grade Tags on the album or album folder.
 */
export const canAddGradeTags = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const allowedAlbumSubtypes = [ALBUM_SUBTYPE.SCHOOLS, ALBUM_SUBTYPE.SPORTS];

  return allowedAlbumSubtypes.includes(entity.album_subtype);
};

/**
 * Determines if the user should be able to add/edit Grade Tags
 * on a specific basename.
 *
 * @param {String} basename - Current basename.
 * @returns {boolean} - Whether the user can add/edit Grade Tags on the album or album folder.
 */
export const canAddGradeTagsOnBasename = basename => {
  const allowedBasenames = [BASENAME.SCHOOLS];

  return allowedBasenames.includes(basename);
};

/**
 * Determines if the user should be able to add/edit Uniform Tags
 * on a specific basename.
 *
 * @param {String} basename - Current basename.
 * @returns {boolean} - Whether the user can add/edit Grade Tags on the album or album folder.
 */
export const canAddUniformTagsOnBasename = basename => {
  const allowedBasenames = [BASENAME.SCHOOLS, BASENAME.SPORTS, BASENAME.PRO];

  return allowedBasenames.includes(basename);
};

/**
 * Determines if the album folder's subtype allows creation of new
 * albums or album folders.
 *
 * @param {Object} albumFolder - The album folder.
 * @param {ALBUM_SUBTYPE} albumFolder.album_subtype - The album folder's subtype.
 * @returns {boolean} - Whether new albums or album folders can be created.
 */
export const canCreateAlbumsAndAlbumFolders = albumFolder => {
  if (!albumFolder || typeof albumFolder.album_subtype === 'undefined') {
    return false;
  }

  // Events, with the exception of weddings, cannot have
  // albums or folders created other than via add-ons.
  if (
    albumFolder.album_type === ALBUM_TYPE.EVENT &&
    albumFolder.album_subtype !== ALBUM_SUBTYPE.WEDDINGS
  ) {
    return false;
  }

  const rejectedAlbumSubtypes = [];

  return !rejectedAlbumSubtypes.includes(albumFolder.album_subtype);
};

/**
 * Determines if an album can be deleted.
 *
 * @param {Object} album - The album.
 * @param {ALBUM_SUBTYPE} album.album_subtype - The album's subtype.
 * @returns {boolean} - Whether the album can be deleted or not.
 */
export const canDeleteAlbum = album => {
  if (!album || typeof album.album_subtype === 'undefined') {
    return false;
  }

  const rejectedAlbumSubtypes = [
    ALBUM_SUBTYPE.EVENTS,
    ALBUM_SUBTYPE.WEDDINGS,
    ALBUM_SUBTYPE.CAMPS,
  ];

  return !rejectedAlbumSubtypes.includes(album.album_subtype);
};

/**
 * Determines whether the album or album folder allows to edit join codes.
 *
 * @param {Object} entity - The album or album folder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The album's or album folder's album_subtype.
 * @returns {boolean} - Whether the album or album folder allows to edit join codes.
 */
export const canEditJoinCode = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const rejectedAlbumSubtypes = [
    ALBUM_SUBTYPE.CAMPS,
    ALBUM_SUBTYPE.SCHOOLS,
    ALBUM_SUBTYPE.GREEK,
  ];

  return !rejectedAlbumSubtypes.includes(entity.album_subtype);
};

/**
 * Determines whether the album/album-folder has access to Revenue dashboard.
 *
 * @param {Object} entity - The album or album folder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The album's or album folder's album_subtype.
 * @returns {boolean} - Whether the album or album folder can access Revenue Dashboard.
 */
export const canAccessRevenueDashboard = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  if (entity.is_demo) {
    return false;
  }

  if (
    entity.search_subscription_credits_joins_enabled ||
    entity.root_album_folder?.search_subscription_credits_joins_enabled
  ) {
    return false;
  }

  if (
    entity.photo_sharing_mode === PHOTO_SHARING_MODE.INTERNAL ||
    entity.root_album_folder?.photo_sharing_mode === PHOTO_SHARING_MODE.INTERNAL
  ) {
    return false;
  }

  return entity.album_type === ALBUM_TYPE.PRO;
};

/**
 * Determines whether the AlbumFolder has permissions to edit the blog.
 *
 * @param {Object} albumFolder - The album folder.
 * @param {Object} albumFolder.parent - The album folder's parent.
 * @returns {boolean} - Whether the album folder can access the Blog.
 */
export const canAccessBlog = albumFolder => {
  if (!albumFolder || typeof albumFolder.album_subtype === 'undefined') {
    return false;
  }

  const allowedSubtypes = [
    ALBUM_SUBTYPE.CAMPS,
    ALBUM_SUBTYPE.SCHOOLS,
    ALBUM_SUBTYPE.GREEK,
    ALBUM_SUBTYPE.SPORTS,
    ALBUM_SUBTYPE.CHURCH,
  ];
  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return (
    allowedSubtypes.includes(albumFolder.album_subtype) &&
    !rejectedAlbumTypes.includes(albumFolder.album_type)
  );
};

/**
 * Determines whether the Album or AlbumFolder has permissions to edit and upload videos.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @returns {boolean} - Whether the album folder can access the Blog.
 */
export const canAccessVideo = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT];

  return !rejectedAlbumTypes.includes(entity.album_type);
};

/**
 * Determines whether the account has access to selfie station.
 *
 * @param {String} basename - The vertical's basename.
 * @returns {boolean} - Whether the album folder can access the Selfie Station.
 */
export const canAccessSelfieStation = basename => {
  const allowedBasenames = [
    BASENAME.CAMPS,
    BASENAME.SCHOOLS,
    BASENAME.PRO,
    BASENAME.SPORTS,
    BASENAME.CHURCH,
  ];

  return allowedBasenames.includes(basename);
};

/**
 * Determines whether a product should be allowed to create new
 * individual albums through the side-button.
 *
 * @param {String} basename Product basename.
 */
export const canCreateIndividualAlbum = basename => {
  const rejectedBasenames = [];

  return !rejectedBasenames.includes(basename);
};

/**
 * Determines whether we should allow multiple tag level
 * selection or not.
 *
 * @param {Album} album The album to check.
 */
export const canAddMultipleLevels = album => {
  if (!album || typeof album.album_subtype === 'undefined') {
    return false;
  }

  const allowedAlbumSubtypes = [ALBUM_SUBTYPE.YOUTH_SPORTS];

  return allowedAlbumSubtypes.includes(album.album_subtype);
};

/**
 * Determines whether a Pro should be allowed to request waldograms.
 *
 * @param {String} basename Product basename.
 */
export const canRequestWaldogram = basename => {
  const allowedBasenames = [BASENAME.CAMPS];

  return allowedBasenames.includes(basename);
};

export const canAddMultipleCoverPhotosForOnboarding = albumFolder => {
  return albumFolder.album_subtype === ALBUM_SUBTYPE.SCHOOLS;
};

/**
 * Determines if an Album allows Photo Group (Sub-album) creation.
 *
 * @param {object} album The album.
 * @param {object} account the account to check
 *
 * @returns {boolean} Whether the album should allow photo groups.
 */
export const albumCanHavePhotoGroup = (album, account = null) => {
  if (!album) {
    return false;
  }

  if (
    typeof account?.pro_settings?.album_photo_groups_enabled !== 'undefined' &&
    account?.pro_settings?.album_photo_groups_enabled !== null
  ) {
    return account?.pro_settings?.album_photo_groups_enabled;
  }

  const rejectedAlbumSubtypes = [
    ALBUM_SUBTYPE.GREEK,
    ALBUM_SUBTYPE.SCHOOLS,
    ALBUM_SUBTYPE.SPORTS,
    ALBUM_SUBTYPE.CHURCH,
    // PRO
    null,
  ];

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT];

  return (
    !rejectedAlbumSubtypes.includes(album?.album_subtype) &&
    !rejectedAlbumTypes.includes(album?.album_type)
  );
};

/**
 * Determines if an event album allows Photo Group (Sub-album) creation.
 *
 * @param {object} album The album.
 * @returns {boolean} Whether the album should allow photo groups.
 */
export const eventAlbumCanHavePhotoGroup = album => {
  if (!album || album?.album_type !== ALBUM_TYPE.EVENT) {
    return false;
  }

  return isWaldofied(album) && album.album_subtype !== ALBUM_SUBTYPE.WEDDINGS;
};

/**
 * Determines if an entity (album/albumFolder) should allow access to the /orders tab.
 *
 * @param {object} entity The album/albumFolder to check for.
 */
export const canAccessOrders = entity => {
  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedAlbumTypes.includes(entity?.album_type);
};

/**
 * Determines if an albumFolder can contain albumFolders.
 *
 * @param {Object} albumFolder The AlbumFolder.
 */
export const canCreateSubFolders = albumFolder => {
  if (!albumFolder) {
    // Generally refers to the albumFolders null state.
    return true;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];
  const rejectedAlbumSubtypes = [ALBUM_SUBTYPE.WEDDINGS];

  return (
    !rejectedAlbumTypes.includes(albumFolder.album_type) &&
    !rejectedAlbumSubtypes.includes(albumFolder.album_subtype)
  );
};

/**
 * Determines if an album allows for brandFilter management..
 *
 * @param {Object} album The Album.
 */
export const canAccessBrandFilter = album => {
  if (!album) {
    return false;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedAlbumTypes.includes(album.album_type);
};

/**
 * Determines whether the entity has web gallery enabled or not.
 * Since shutterbug now handles Web Gallery in a different manner (in PM)
 * it does not need to be included in this check anymore.
 *
 * @param {Object} entity The album or albumFolder.
 */
export const canAccessWebGalleries = entity =>
  entity?.is_web_gallery &&
  ![ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT].includes(entity?.album_type);

/**
 * Determines whether the user can either access their Matches or
 * onboard via a Web Gallery.
 *
 * @param {Object} entity The Album or AlbumFolder web gallery.
 * @returns {boolean}
 */
export const canAccessMyMatchesFromWebGallery = entity => {
  return !!entity?.joins_enabled;
};

/**
 * Whether the user can joined content based on basename.
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessJoinedContentOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the Album or AlbumFolder can access Waldograms for Business.
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessBusinessWaldograms = entity => {
  if (!entity) {
    return false;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedAlbumTypes.includes(entity.album_type);
};

/**
 * Whether the Album or AlbumFolder can access Mass Comms.
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessMassComms = entity => {
  if (!entity) {
    return false;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedAlbumTypes.includes(entity.album_type);
};

/**
 * Whether the Album or AlbumFolder can access Reference Photos Albums
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessReferencePhotosAlbums = entity => {
  if (!entity) {
    return false;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT];

  return !rejectedAlbumTypes.includes(entity.album_type);
};

/**
 * Whether the Album or AlbumFolder can access Waldograms for Business (on basename).
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessBusinessWaldogramsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the basename has access to bulk Waldogram tier selection.
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessBulkWaldogramTiersOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Determines whether the vertical has access to the Address Book feature.
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessAddressBookOnBasename = basename => {
  // TODO [cards] remove this entirely if we're keeping it open to everyone
  return !!basename;
};

/**
 * Returns true or false depending on the basename.
 * Eg camps/schools: False, pro/sbug: True.
 *
 * @param {string} basename The product vertical from the URL.
 * @returns {boolean} Whether albums require a location for that product vertical.
 *
 */
export const isLocationRequiredOnAlbums = basename =>
  [BASENAME.PRO, BASENAME.SHUTTERBUG].includes(basename);

/**
 * Determines if a user can update their password or not.
 *
 * @param {Object} account The account to check for permission.
 * @returns {boolean} Whether the user can update their password or not.
 */
export const canUpdatePassword = account => account.has_password;

/**
 * Feature block for magic link.
 */
export const canRequestMagicLink = () => false;

/**
 * Determines if a user can access the "No post to social" feature.
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessNoPostToSocial = entity => {
  const allowedAlbumTypes = [ALBUM_TYPE.PRO];

  return allowedAlbumTypes.includes(entity?.album_type);
};

/**
 * Determines if a user can access the "No post to social" feature (on basename).
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessNoPostToSocialOnBasename = basename => {
  const rejectedBasenames = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Determines if the user should be able to report photos on
 * an Album or AlbumFolder.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The Album or AlbumFolder subtype.
 * @returns {boolean}
 */
export const canReportPhotos = entity => {
  const allowedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return allowedAlbumTypes.includes(entity?.album_type);
};

/**
 * Determines if the user should be able to choose upload permissions on
 * an Album or AlbumFolder.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @param {ALBUM_TYPE} entity.album_type - The Album or AlbumFolder type.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The Album or AlbumFolder subtype.
 * @returns {boolean}
 */
export const canSelectUploadPermissions = entity => {
  const allowedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];
  const allowedAlbumSubtypes = [ALBUM_SUBTYPE.GREEK];

  return (
    allowedAlbumTypes.includes(entity?.album_type) ||
    allowedAlbumSubtypes.includes(entity?.album_subtype)
  );
};

/**
 * Determines if the user should be able to view DNP identities
 * on Album or AlbumFolders.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The Album's or AlbumFolder's subtype.
 * @returns {boolean}
 */
export const canViewDNPIdentities = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const allowedAlbumSubtypes = [ALBUM_SUBTYPE.SCHOOLS, ALBUM_SUBTYPE.CAMPS];

  return allowedAlbumSubtypes.includes(entity.album_subtype);
};

/**
 * Determines if the social posts filter should be visible for the entity.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The Album's or AlbumFolder's subtype.
 * @returns {boolean}
 */
export const canViewSocialPostsFilter = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const allowedAlbumSubtypes = [ALBUM_SUBTYPE.SCHOOLS, ALBUM_SUBTYPE.CAMPS];

  return allowedAlbumSubtypes.includes(entity.album_subtype);
};

/**
 * Determines if the option to price a gallery with search credits is available for
 * the user.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The Album's or AlbumFolder's subtype.
 * @returns {boolean}
 */
export const canPriceWithSearchCredits = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const allowedAlbumSubtypes = [ALBUM_SUBTYPE.SPORTS, ALBUM_SUBTYPE.CHURCH];

  return allowedAlbumSubtypes.includes(entity.album_subtype);
};

/**
 * Determines if the option to add / manage picture day albums is available to the user.
 *
 * @param {Object} basename - The current basename.
 * @returns {boolean}
 */
export const canAccessPictureDay = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.PRO, BASENAME.SPORTS, BASENAME.SCHOOLS];

  return allowedBasenames.includes(basename);
};

/**
 * Determines if the option add create album permissions is available based on the album type.
 *
 * @param {Object} entity - The Album or AlbumFolder.
 * @param {ALBUM_SUBTYPE} entity.album_subtype - The Album's or AlbumFolder's subtype.
 * @param {Boolean} entity.is_demo - Whether or not the AlbumFolder is PF or not.
 * @returns {boolean}
 */
export const canAddCreateAlbumPermissions = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  // Special case. Since subtype === NULL is the same for PRO events and for
  // Custom Events, we will allow this exception regardless of allowed subtypes.
  if (entity.album_type === ALBUM_TYPE.PRO && entity.album_subtype === null) {
    return true;
  }

  const allowedAlbumSubTypes = [
    ALBUM_SUBTYPE.RESORTS,
    ALBUM_SUBTYPE.SCHOOLS,
    ALBUM_SUBTYPE.CAMPS,
    ALBUM_SUBTYPE.GREEK,
    ALBUM_SUBTYPE.SPORTS,
    ALBUM_SUBTYPE.YOUTH_SPORTS,
    ALBUM_SUBTYPE.CHURCH,
  ];

  return allowedAlbumSubTypes.includes(entity.album_subtype);
};

// New flow conditionals:
export const canAccessShutterbugDelivery = basename => {
  // Camps now allow for Shutterbug Delivery
  const rejectedBasenames = [BASENAME.PRO, BASENAME.EVENTS, BASENAME.GREEK];

  return !rejectedBasenames.includes(basename);
};

// New flow conditionals:
export const canAccessPhotoFinderPlusPPGDelivery = basename => {
  // Camps now allow for Shutterbug Delivery
  const allowedBasenames = [BASENAME.SCHOOLS];

  return allowedBasenames.includes(basename);
};

export const canAccessParentPayDelivery = basename => {
  const allowedBasenames = [BASENAME.CAMPS];
  return allowedBasenames.includes(basename);
};

export const canAccessFreeWebGalleryDelivery = (basename, user = null) => {
  const rejectedBasenames = [BASENAME.PRO, BASENAME.SHUTTERBUG];
  return (
    !rejectedBasenames.includes(basename) ||
    user?.pro_registration_persona === PRO_REGISTRATION_PERSONA_VALUES.EVENTS ||
    (user?.roles ?? []).includes(ACCOUNT_PRO_ROLES.EVENT_ORGANIZER)
  );
};

export const canAccessInternalOnlyDelivery = basename => {
  const rejectedBasenames = [BASENAME.PRO];
  return !rejectedBasenames.includes(basename);
};

/**
 * Determines whether the user can access Picture Day ("Sell Photos")
 * based on basename.
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessPictureDayOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.CAMPS, BASENAME.SCHOOLS, BASENAME.SPORTS];
  return !rejectedBasenames.includes(basename);
};

/**
 * Determines whether the user can access Picture Day ("Sell Photos")
 * based on basename.
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canAccessWeddingsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.PRO, BASENAME.EVENTS, BASENAME.SHUTTERBUG];
  return allowedBasenames.includes(basename);
};

/**
 * Determines whether the user can access Picture Day ("Sell Photos")
 * based on account settings.
 *
 * @param {Object} account The Account.
 * @returns {boolean}
 */
export const canAccessPhotoSellingOnAccount = account => {
  return account?.pro_settings?.sell_photos_enabled;
};

/**
 * Determines whether the user can access "Share Potos"
 * based on account settings.
 *
 * @param {Object} account The Account.
 * @returns {boolean}
 */
export const canAccessPhotoSharingOnAccount = account => {
  return account?.pro_settings?.share_photos_enabled;
};

/**
 * Determines whether the entity is yearbook or not
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @param {Boolean} allowSchoolPortraitsWithoutExport Wether or not to check for the other School Portraits category.
 * @returns {boolean}
 */
export const isYearbookEntity = (
  entity,
  allowSchoolPortraitsWithoutExport = false,
) => {
  if (isAlbum(entity) && !entity?.pricing_categories?.records?.length) {
    return !!(
      entity?.root_album_folder?.pricing_categories?.records ?? []
    ).find(cat =>
      allowSchoolPortraitsWithoutExport
        ? (cat?.pricing_category?.name ?? '').includes('School Portraits')
        : (cat?.pricing_category?.name ?? '').includes(
            'Including Yearbook Export',
          ),
    );
  }

  return !!(entity?.pricing_categories?.records ?? []).find(cat =>
    allowSchoolPortraitsWithoutExport
      ? (cat?.pricing_category?.name ?? '').includes('School Portraits')
      : (cat?.pricing_category?.name ?? '').includes(
          'Including Yearbook Export',
        ),
  );
};

/**
 * Determines whether the user can import members via CSV.
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canImportMembersViaCsv = entity => {
  if (!entity) {
    return false;
  }

  // There is no purpose on adding identities if the albumFolder
  // is not JOIN ALL (except for PSPA), as these would not be joined to the albums.
  if (
    !isAlbum(entity) &&
    !isYearbookEntity(entity) &&
    entity.join_type === ALBUM_FOLDER_JOIN_TYPE.INDIVIDUAL
  ) {
    return false;
  }

  // Similar to above. If it's JOIN_ALL, we don't need album level.
  // This should, of course, except individual albums.
  if (
    isAlbum(entity) &&
    !entity.root_album_folder.individual_albums &&
    entity.root_album_folder?.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL
  ) {
    return false;
  }

  if (
    entity.picture_day_enabled ||
    entity.root_album_folder?.picture_day_enabled
  ) {
    return true;
  }

  // Photo Finder Credits + PPG
  if (
    [
      PHOTO_SHARING_MODE.PHOTO_FINDER,
      PHOTO_SHARING_MODE.WEB_GALLERY_AND_PHOTO_FINDER,
    ].includes(entity.photo_sharing_mode) &&
    !entity?.is_demo &&
    entity?.org_pay_photo_pricing_enabled &&
    entity?.price?.value > 0
  ) {
    return true;
  }

  // Photo Finder Credits + PPA
  if (
    isAlbum(entity) &&
    !entity?.is_demo &&
    entity.root_album_folder?.album_price_mode === ALBUM_PRICE_MODE.PPA_ONLY &&
    entity?.price_whole_album?.value > 0
  ) {
    return true;
  }

  if (!entity.is_demo) {
    return false;
  }

  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedAlbumTypes.includes(entity.album_type);
};

/**
 * Determines whether the user can import members via CSV (basename version).
 *
 * @param {BASENAME} basename The basename.
 * @returns {boolean}
 */
export const canImportMembersViaCsvOnBasename = basename => {
  const rejectedBasenames = [BASENAME.SHUTTERBUG];
  return !rejectedBasenames.includes(basename);
};

/**
 * Determines whether the user can import members via CSV (user version).
 *
 * @param {Object} account The account
 * @returns {boolean}
 */
export const canImportMembersViaCsvOnAccount = account => {
  // Momentarily disable this check so everyone can upload a CSV regardless of currency.
  if (true) {
    return true;
  }

  return account?.pro_settings?.default_currency === CURRENCY_CODE.USD;
};

/**
 * Determines whether the user has access to marketing materials.
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessMarketingMaterials = entity => {
  const rejectedAlbumTypes = [ALBUM_TYPE.SHUTTERBUG];

  return !rejectedAlbumTypes.includes(entity.album_type);
};

/**
 * Determines whether the entity can access PSPA exports
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessPSPAExport = entity => {
  return isYearbookEntity(entity);
};

/**
 * Determines whether the user has access to My faces view.
 *
 * @param {Object} basename The active basename.
 * @returns {boolean}
 */
export const canAccessMyFacesOnBasename = basename => {
  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access spotlights on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessSpotlightOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can access highlights on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessHighlightsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can access matches only setting on Share photos
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessMatchesOnly = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.PRO];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the basename has access to purchased events
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessNotificationsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access account print provider based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountPrintProviderOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can access account stripe connect based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessStripeConnectOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const acceptedBasenames = [BASENAME.PRO];

  return acceptedBasenames.includes(basename);
};

/**
 * Whether the user can access account taxes based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountTaxesOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.PRO];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access GDPR data based on basename.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessGDPROnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can notification settings on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessPurchasedEvents = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.PRO, BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Determines if the user is allowed to upload through the
 * gallery/folders or can only do so through albums.
 *
 * @param {BASENAME} entity - The album/albumFolder.
 * @returns {boolean}
 */
export const canUploadToChildAlbumFolders = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const rejectedAlbumSubtypes = [ALBUM_SUBTYPE.WEDDINGS];
  const rejectedAlbumTypes = [ALBUM_TYPE.EVENT, ALBUM_TYPE.SHUTTERBUG];

  return (
    !rejectedAlbumTypes.includes(entity.album_type) &&
    !rejectedAlbumSubtypes.includes(entity.album_subtype)
  );
};

/**
 * Determines if the the user is allowed to upload
 * photos and videos at the same time.
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canUploadPhotosAndVideosBundled = basename => {
  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access slideshows based on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessSlideshowsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [];

  return !rejectedBasenames.includes(basename);
};

/**
 * Determines if an entity can be shared based on photo_sharing_group.
 *
 * @param {Object} entity The AlbumFolder or Album.
 */
export const canShareEntity = entity => {
  if (!entity) {
    return false;
  }

  const photoSharingMode =
    entity.photo_sharing_mode ||
    entity.root?.photo_sharing_mode ||
    entity.root_album_folder?.photo_sharing_mode;

  const isShareable =
    entity.joins_enabled ||
    entity.is_web_gallery ||
    entity.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE ||
    entity.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC ||
    entity.all_photos_mode === ALL_PHOTOS_MODE.NONE;

  return photoSharingMode !== PHOTO_SHARING_MODE.INTERNAL && isShareable;
};

/**
 * Determines if an entity allows for member deletion
 *
 * @param {Object} entity the albumFolder or Album
 * @returns {boolean}
 */
export const canRemoveMembers = entity => {
  if (!entity) {
    return false;
  }

  // Only allow PF-enabled entities.
  return entity.is_demo && entity.org_pay_photo_pricing_enabled;
};

/**
 * Determines whether the entity has access to PLIC Books integrations.
 *
 * @param {Object} entity The Album or AlbumFolder.
 * @returns {boolean}
 */
export const canAccessPlicBooksIntegration = entity => {
  if (!entity || typeof entity.album_subtype === 'undefined') {
    return false;
  }

  const allowedSubtypes = [ALBUM_SUBTYPE.SCHOOLS];

  return allowedSubtypes.includes(entity.album_subtype);
};

/**
 * Determines if the active basename supports Freemium / A la carte events.
 *
 * @param {String} basename Active basename.
 * @returns {boolean|*|boolean}
 */
export const canCreateFreemiumEvents = basename => {
  if (!basename) {
    return false;
  }

  // Only allow PF-enabled entities.
  return [BASENAME.PRO, BASENAME.EVENTS, BASENAME.GREEK].includes(basename);
};

/**
 * Whether the user can Marketing Materials modal on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessMarketingMaterialModalOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

/**
 * Whether the user can access account matches on current basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessAccountMatchesOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const allowedBasenames = [BASENAME.SHUTTERBUG];

  return allowedBasenames.includes(basename);
};

export const canAccessMemberConnectOnBasename = basename => {
  if (!basename) {
    return false;
  }

  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};

/**
 * Whether the user can purchase event addons
 *
 * @param {EVENT_ADDON_ITEM_TYPE} eventAddonType - The event addon type.
 * @param {ALBUM_SUBTYPE} albumSubtype - The album subtype.
 * @returns {boolean}
 */
export const canPurchaseEventAddon = (eventAddonType, albumSubtype) => {
  if (
    [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    ].includes(eventAddonType)
  ) {
    return true;
  }

  if (
    [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
    ].includes(eventAddonType)
  ) {
    return albumSubtype === ALBUM_SUBTYPE.EVENTS;
  }

  if ([EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO].includes(eventAddonType)) {
    return true;
  }

  return false;
};

/**
 * Whether the user is able to access photoFinderCredits event create on the given basename
 *
 * @param {BASENAME} basename - The basename
 * @param {Object} user - The logged user
 *
 * @returns {boolean}
 */
export const canAccessPhotoFinderCreditsOnBasename = (
  basename,
  user = null,
) => {
  if (!user) {
    return ![BASENAME.PRO].includes(basename);
  }

  return (
    ![BASENAME.PRO].includes(basename) ||
    user?.pro_registration_persona === PRO_REGISTRATION_PERSONA_VALUES.EVENTS ||
    (user?.roles ?? []).includes(ACCOUNT_PRO_ROLES.EVENT_ORGANIZER)
  );
};

/**
 * Whether the user can access original downloads on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessOriginalDownloadsOnBasename = basename => {
  if (!basename) {
    return false;
  }

  return ![BASENAME.SHUTTERBUG].includes(basename);
};

/**
 * Whether the user can access foreground update on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessForegroundUpdateOnBasename = basename => {
  if (!basename) {
    return false;
  }

  return basename === BASENAME.PRO;
};

/**
 * Whether the user can access school fields on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessSchoolFields = basename => {
  if (!basename) {
    return false;
  }

  return [BASENAME.PRO, BASENAME.SCHOOLS].includes(basename);
};

/**
 * Whether the user can access school code on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessSchoolCode = basename => {
  if (!basename) {
    return false;
  }

  return ![
    BASENAME.CAMPS,
    BASENAME.CHURCH,
    BASENAME.SPORTS,
    BASENAME.GREEK,
    BASENAME.RESORTS,
  ].includes(basename);
};

/**
 * Whether the user can access event collages on basename
 *
 * @param {BASENAME} basename - The basename.
 * @returns {boolean}
 */
export const canAccessEventCollages = basename => {
  if (!basename) {
    return false;
  }

  return [BASENAME.CAMPS, BASENAME.SCHOOLS].includes(basename);
};

/**
 * Checks if a basename allows the Testing Sandbox feature.
 * Testing Sandbox is available to all PRO basenames (excluding SHUTTERBUG).
 *
 * @param {BASENAME} basename - The basename to check.
 * @returns {boolean} - True if the basename allows Testing Sandbox, false otherwise.
 */
export const canAccessTestingSandboxOnBasename = basename => {
  if (!basename) {
    return false;
  }
  const rejectedBasenames = [BASENAME.SHUTTERBUG];

  return !rejectedBasenames.includes(basename);
};
